import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DomainIcon from '@mui/icons-material/Domain';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LinkIcon from '@mui/icons-material/Link';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  ListItemButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const emptyFilter = {
  dateRange: [{ startDate: null, endDate: null, key: 'selection' }],
  name: '',
  offerLink: '',
  offerId: '',
  status: null,
  redirectDomain: null,
  entryDomain: null,
  trafficSource: null,
};

const DomainFilterInput = ({ mode, setMode, value, setValue, label, domainOptions, onEnter }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Select
      size="small"
      value={mode}
      onChange={(e) => {
        setMode(e.target.value);
        setValue(null);
      }}
    >
      <MenuItem value="is">Is</MenuItem>
      <MenuItem value="contains">Contains</MenuItem>
    </Select>
    {mode === 'is' ? (
      <Autocomplete
        size="small"
        options={domainOptions}
        value={value}
        onChange={(_, val) => setValue(val)}
        getOptionLabel={(opt) => opt.label || ''}
        isOptionEqualToValue={(opt, val) => opt._id === val._id}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
    ) : (
      <TextField
        size="small"
        fullWidth
        label={`Search ${label}`}
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter()}
      />
    )}
  </Box>
);

const OfferFilter = ({ domains = [], traffic = [], onApply, appliedFilter = emptyFilter }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const [pendingDateRange, setPendingDateRange] = useState(appliedFilter.dateRange);
  const [pendingName, setPendingName] = useState(appliedFilter.name);
  const [pendingOfferLink, setPendingOfferLink] = useState(appliedFilter.offerLink);
  const [pendingOfferId, setPendingOfferId] = useState(appliedFilter.offerId);
  const [pendingStatus, setPendingStatus] = useState(appliedFilter.status ?? null);
  const [trafficSourceMode, setTrafficSourceMode] = useState(appliedFilter.trafficSource?.mode || 'is');
  const [trafficSourceValue, setTrafficSourceValue] = useState(appliedFilter.trafficSource?.value || null);

  const [redirectDomainMode, setRedirectDomainMode] = useState(appliedFilter.redirectDomain?.mode || 'is');
  const [redirectDomainValue, setRedirectDomainValue] = useState(appliedFilter.redirectDomain?.value || null);

  const [entryDomainMode, setEntryDomainMode] = useState(appliedFilter.entryDomain?.mode || 'is');
  const [entryDomainValue, setEntryDomainValue] = useState(appliedFilter.entryDomain?.value || null);

  useEffect(() => {
    setPendingDateRange(appliedFilter.dateRange);
    setPendingName(appliedFilter.name);
    setPendingOfferLink(appliedFilter.offerLink);
    setPendingOfferId(appliedFilter.offerId);
    setPendingStatus(appliedFilter.status ?? null);
    setTrafficSourceMode(appliedFilter.trafficSource?.mode || 'is');
    setTrafficSourceValue(appliedFilter.trafficSource?.value || null);
    setRedirectDomainMode(appliedFilter.redirectDomain?.mode || 'is');
    setRedirectDomainValue(appliedFilter.redirectDomain?.value || null);
    setEntryDomainMode(appliedFilter.entryDomain?.mode || 'is');
    setEntryDomainValue(appliedFilter.entryDomain?.value || null);
  }, [appliedFilter]);

  const toggle = (section) => setExpanded((prev) => (prev === section ? null : section));

  const chevronSx = (section) => ({
    flexShrink: 0,
    transform: expanded === section ? 'rotate(180deg)' : 'none',
    transition: 'transform 0.2s',
  });

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    onApply({
      dateRange: pendingDateRange,
      name: pendingName,
      offerLink: pendingOfferLink,
      offerId: pendingOfferId,
      status: pendingStatus,
      redirectDomain: redirectDomainValue ? { mode: redirectDomainMode, value: redirectDomainValue } : null,
      entryDomain: entryDomainValue ? { mode: entryDomainMode, value: entryDomainValue } : null,
      trafficSource: trafficSourceValue ? { mode: trafficSourceMode, value: trafficSourceValue } : null,
    });
    handleClose();
  };

  const handleReset = () => {
    setPendingDateRange(emptyFilter.dateRange);
    setPendingName('');
    setPendingOfferLink('');
    setPendingOfferId('');
    setPendingStatus(null);
    setTrafficSourceMode('is');
    setTrafficSourceValue(null);
    setRedirectDomainMode('is');
    setRedirectDomainValue(null);
    setEntryDomainMode('is');
    setEntryDomainValue(null);
    onApply(emptyFilter);
    handleClose();
  };

  const domainOptions = domains
    .filter((d) => !d.isEntry)
    .map((d) => ({ label: d.domainName || d.domainLink, ...d }));

  const entryDomainOptions = domains
    .filter((d) => d.isEntry)
    .map((d) => ({ label: d.domainName || d.domainLink, ...d }));

  const trafficOptions = traffic.map((t) => ({
    label: t.name,
    ...t,
  }));

  return (
    <>
      <Button variant="text" startIcon={<FilterListIcon />} onClick={handleOpen}>
        Filter
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper elevation={0} sx={{ minWidth: 300 }}>
          {/* Created At */}
          <ListItemButton onClick={() => toggle('date')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body2">Created At</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('date')} />
          </ListItemButton>
          <Collapse in={expanded === 'date'}>
            <Box sx={{ pb: 1 }}>
              <DateRange
                ranges={pendingDateRange}
                onChange={(item) => setPendingDateRange([item.selection])}
                maxDate={new Date()}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Offer Name */}
          <ListItemButton onClick={() => toggle('name')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LabelOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Offer Name</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('name')} />
          </ListItemButton>
          <Collapse in={expanded === 'name'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Offer Name"
                value={pendingName}
                onChange={(e) => setPendingName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Offer Link */}
          <ListItemButton onClick={() => toggle('offerlink')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LinkIcon fontSize="small" color="action" />
              <Typography variant="body2">Offer Link</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('offerlink')} />
          </ListItemButton>
          <Collapse in={expanded === 'offerlink'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Offer Link"
                value={pendingOfferLink}
                onChange={(e) => setPendingOfferLink(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Offer ID */}
          <ListItemButton onClick={() => toggle('offerid')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <FingerprintIcon fontSize="small" color="action" />
              <Typography variant="body2">Offer ID</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('offerid')} />
          </ListItemButton>
          <Collapse in={expanded === 'offerid'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <TextField
                size="small"
                fullWidth
                label="Offer ID"
                value={pendingOfferId}
                onChange={(e) => setPendingOfferId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Status */}
          <ListItemButton onClick={() => toggle('status')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LabelOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Status</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('status')} />
          </ListItemButton>
          <Collapse in={expanded === 'status'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={pendingStatus || ''}
                onChange={(e) => setPendingStatus(e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </Box>
          </Collapse>

          <Divider />

          {/* Redirect Domain */}
          <ListItemButton onClick={() => toggle('redirectdomain')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <DomainIcon fontSize="small" color="action" />
              <Typography variant="body2">Redirect Domain</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('redirectdomain')} />
          </ListItemButton>
          <Collapse in={expanded === 'redirectdomain'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <DomainFilterInput
                mode={redirectDomainMode}
                setMode={setRedirectDomainMode}
                value={redirectDomainValue}
                setValue={setRedirectDomainValue}
                label="Redirect Domain"
                domainOptions={domainOptions}
                onEnter={handleApply}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Entry Domain */}
          <ListItemButton onClick={() => toggle('entrydomain')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <DomainIcon fontSize="small" color="action" />
              <Typography variant="body2">Entry Domain</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('entrydomain')} />
          </ListItemButton>
          <Collapse in={expanded === 'entrydomain'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <DomainFilterInput
                mode={entryDomainMode}
                setMode={setEntryDomainMode}
                value={entryDomainValue}
                setValue={setEntryDomainValue}
                label="Entry Domain"
                domainOptions={entryDomainOptions}
                onEnter={handleApply}
              />
            </Box>
          </Collapse>

          <Divider />

          {/* Traffic Source */}
          <ListItemButton onClick={() => toggle('traffic')} sx={{ px: 2, py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <LabelOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">Traffic Source</Typography>
            </Box>
            <ExpandMoreIcon fontSize="small" sx={chevronSx('traffic')} />
          </ListItemButton>
          <Collapse in={expanded === 'traffic'}>
            <Box sx={{ px: 2, pb: 2, minWidth: 260 }}>
              <DomainFilterInput
                mode={trafficSourceMode}
                setMode={setTrafficSourceMode}
                value={trafficSourceValue}
                setValue={setTrafficSourceValue}
                label="Traffic Source"
                domainOptions={trafficOptions}
                onEnter={handleApply}
              />
            </Box>
          </Collapse>

          <Divider />

          <Box display="flex" justifyContent="space-between" px={2} py={1.5}>
            <Button size="small" onClick={handleReset}>
              Reset
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default OfferFilter;
